import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useAISalesDock } from "@/components/AISalesDock";
import { ReferEarn } from "@/components/ReferEarn";
import { AutoTopUpNudge } from "@/components/AutoTopUpNudge";
import { InstallButton } from "@/components/PWAInstall";
import {
  ArrowsClockwise, GlobeHemisphereEast, SimCard, Users, Printer as Fax, VideoCamera,
  DotsThreeCircle, Plus, ArrowUpRight, ArrowDownLeft, Gauge as GaugeHigh, WhatsappLogo,
  MagnifyingGlass, PaperPlaneTilt, Car, ArrowRight, Briefcase, ForkKnife, Truck, Warning, Sparkle, Gift, DeviceMobile,
} from "@phosphor-icons/react";

const flagship = [
  { to: "/app/topup", label: "Send Top-up", icon: ArrowsClockwise, tint: "bg-[#059669] text-white", sub: "Airtime, data, bills & gift cards home in seconds", badge: "Most popular" },
  { to: "/app/giftcards", label: "Gift Cards", icon: Gift, tint: "bg-[#DB2777] text-white", sub: "Send a gift card by email in seconds", badge: "New" },
  { to: "/app/esim", label: "eSIM", icon: SimCard, tint: "bg-[#FF7A00] text-white", sub: "Instant travel data in 190+ destinations" },
  { to: "/app/numbers", label: "Global Numbers", icon: GlobeHemisphereEast, tint: "bg-[#059669] text-white", sub: "Get a local number abroad — calls & texts" },
  { to: "/app/whatsapp", label: "WhatsApp Business", icon: WhatsappLogo, tint: "bg-[#25D366] text-white", sub: "Reach customers where they already chat" },
  { to: "/app/rides", label: "iYeneh Ride", icon: Car, tint: "bg-[#0EA5E9] text-white", sub: "Book rides or drive to earn in your city" },
  { to: "/app/pbx", label: "Cloud PBX + SI Receptionist", icon: Users, tint: "bg-[#7C3AED] text-white", sub: "Extensions, IVR & SI receptionist for business" },
];
const secondary = [
  { to: "/app/numbers", label: "Buy Number", icon: GlobeHemisphereEast },
  { to: "/app/whatsapp", label: "WhatsApp", icon: WhatsappLogo },
  { to: "/app/esim", label: "eSIM", icon: SimCard },
  { to: "/app/lookup", label: "Number Lookup", icon: MagnifyingGlass },
  { to: "/app/send", label: "Send Money", icon: PaperPlaneTilt },
  { to: "/app/giftcards", label: "Gift Cards", icon: Gift },
  { to: "/app/airtime", label: "Airtime", icon: DeviceMobile },
  { to: "/app/freelance", label: "Freelance", icon: Briefcase },
  { to: "/app/eat", label: "iYeneh Eat", icon: ForkKnife },
  { to: "/app/fleet", label: "Fleet", icon: Truck },
  { to: "/app/pbx", label: "PBX", icon: Users },
  { to: "/app/fax", label: "Fax", icon: Fax },
  { to: "/app/meet", label: "Meet", icon: VideoCamera },
  { to: "/app/more", label: "More", icon: DotsThreeCircle },
];

export default function Home() {
  const navigate = useNavigate();
  const { user, isStaff } = useAuth();
  const { open: openAssistant } = useAISalesDock();
  const { data: wallet } = useQuery({ queryKey: ["wallet"], queryFn: async () => (await api.get("/wallet")).data });
  const { data: ledger } = useQuery({ queryKey: ["ledger"], queryFn: async () => (await api.get("/wallet/ledger")).data });
  const { data: myDids } = useQuery({ queryKey: ["dids-mine"], queryFn: async () => (await api.get("/dids/mine")).data });
  const { data: myExtData } = useQuery({ queryKey: ["my-extension"], queryFn: async () => (await api.get("/pbx/my-extension")).data });
  const myExtension = myExtData?.extension;
  const hasNumber = (myDids?.dids?.length || 0) > 0;
  const entries = ledger?.entries?.slice(0, 5) || [];

  return (
    <div className="space-y-8">
      {/* greeting */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm text-muted-foreground">Welcome back</p>
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">{user?.name?.split(" ")[0] || "there"} 👋</h1>
          {user?.member_no != null ? (
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <span data-testid="home-member-badge"
                className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                <Users size={13} weight="fill" /> Member #{user.member_no}
              </span>
              <button onClick={() => navigate("/dialer")} data-testid="dashboard-download-dialer" className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground transition-transform hover:-translate-y-0.5">Download iYeneh Dialer</button>
            </div>
          ) : (
            <div className="mt-3">
              <button onClick={() => navigate("/dialer")} data-testid="dashboard-download-dialer" className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground transition-transform hover:-translate-y-0.5">Download iYeneh Dialer</button>
            </div>
          )}
        </div>
        {isStaff && (
          <button data-testid="home-command-center" onClick={() => navigate("/command")}
            className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium transition-colors hover:border-primary">
            <GaugeHigh size={16} weight="duotone" className="text-primary" /> Command Center
          </button>
        )}
      </div>

      {myExtension && (
        <button data-testid="home-my-extension" onClick={() => navigate("/app/call")}
          className="flex w-full items-center justify-between gap-4 rounded-2xl border border-[#00A6F7]/30 bg-[#00A6F7]/5 p-4 text-left transition-colors hover:bg-[#00A6F7]/10">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#00A6F7] text-white"><Users size={20} weight="duotone" /></div>
            <div>
              <p className="text-sm font-semibold">Your extension · {myExtension.ext}</p>
              <p className="text-xs text-muted-foreground">
                {myExtension.name}{myExtension.department_label && myExtension.department_label !== "—" ? ` · ${myExtension.department_label}` : ""} — colleagues dial this to ring your device
              </p>
            </div>
          </div>
          <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${myExtension.online ? "bg-emerald-500/10 text-emerald-600" : "bg-secondary text-muted-foreground"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${myExtension.online ? "bg-emerald-500" : "bg-muted-foreground/40"}`} /> {myExtension.online ? "Online" : "Offline"}
          </span>
        </button>
      )}

      {/* wallet hero */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#08111F] p-7 text-white">
        <div className="grain absolute inset-0" />
        <div className="relative flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-white/50">Universal Wallet</p>
            <p data-testid="home-balance" className="mt-2 font-mono-metric text-5xl font-semibold tracking-tight">
              {wallet ? money(wallet.balance_cents, wallet.currency) : "—"}
            </p>
            <p className="mt-1 text-sm text-white/50">Pays for every service across the platform</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button data-testid="home-add-funds" onClick={() => navigate("/app/wallet")}
              className="flex items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold text-white transition-transform hover:scale-105" style={{ background: "#FF7A00" }}>
              <Plus size={18} weight="bold" /> Add Funds
            </button>
          </div>
        </div>
      </div>

      {/* iYeneh Assistant entry point */}
      <div data-testid="home-assistant-entry" className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-primary/30 bg-primary/5 p-6">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary"><Sparkle size={26} weight="fill" /></div>
          <div>
            <h2 className="font-display text-xl font-semibold">What would you like to accomplish today?</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">Your iYeneh Assistant finds the right service and sets you up in seconds.</p>
          </div>
        </div>
        <button data-testid="home-ask-iyeneh" onClick={() => openAssistant()}
          className="flex items-center gap-2 rounded-full bg-primary px-5 py-3 font-semibold text-primary-foreground transition-transform hover:scale-105">
          <Sparkle size={18} weight="fill" /> Ask iYeneh
        </button>
      </div>

      {/* low credit nudge — only after they've funded at least once */}
      {!isStaff && wallet?.has_topped_up && wallet.balance_cents < 300 && (
        <div data-testid="low-credit-nudge" className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#FF7A00]/40 bg-[#FF7A00]/10 p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FF7A00] text-white">
              <Warning size={20} weight="duotone" />
            </div>
            <div>
              <p className="text-sm font-semibold">Low balance — {money(wallet.balance_cents, wallet.currency)} left</p>
              <p className="mt-0.5 text-xs text-muted-foreground">Top up to keep calls, texts &amp; top-ups running without interruption.</p>
            </div>
          </div>
          <button data-testid="low-credit-cta" onClick={() => navigate("/app/wallet")}
            className="flex items-center gap-2 rounded-full bg-[#FF7A00] px-4 py-2 text-sm font-semibold text-white transition-transform hover:scale-105">
            <Plus size={15} weight="bold" /> Add Funds
          </button>
        </div>
      )}

      {/* 3-step get started funnel */}
      {!isStaff && (
        <div className="rounded-3xl border border-border bg-card p-5" data-testid="get-started-3step">
          <h2 className="font-display text-lg font-semibold">Start in 3 simple steps</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {[
              { n: 1, done: true, title: "You're signed in", sub: "Your account is ready", cta: null },
              { n: 2, done: (wallet?.balance_cents || 0) > 0, title: "Add funds", sub: "Load your wallet to pay for anything", to: "/app/wallet", label: "Add Funds" },
              { n: 3, done: false, title: "Send or call", sub: "Top-up, call or text in one tap", to: "/app/topup", label: "Send a Top-Up" },
            ].map((step) => (
              <div key={step.n} className={`rounded-2xl border p-4 ${step.done ? "border-emerald-300 bg-emerald-50/60" : "border-border"}`}>
                <div className="flex items-center gap-2">
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${step.done ? "bg-emerald-500 text-white" : "bg-primary text-primary-foreground"}`}>{step.done ? "✓" : step.n}</span>
                  <p className="font-semibold">{step.title}</p>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{step.sub}</p>
                {step.to ? (
                  <button data-testid={`step-${step.n}-cta`} onClick={() => navigate(step.to)}
                    className="mt-3 w-full rounded-full bg-primary py-2 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02]">{step.label}</button>
                ) : (
                  <span className="mt-3 inline-block rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-600">Done</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* numbers empty-state nudge */}
      {!isStaff && myDids && !hasNumber && (
        <div data-testid="numbers-empty-state" className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-[#059669]/30 bg-[#059669]/5 p-5">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#059669] text-white">
              <GlobeHemisphereEast size={24} weight="duotone" />
            </div>
            <div>
              <p className="font-display text-base font-semibold">Get your first phone number</p>
              <p className="mt-0.5 text-sm text-muted-foreground">A local number unlocks calls, texts &amp; Cloud PBX — pick one in under a minute.</p>
            </div>
          </div>
          <button data-testid="numbers-empty-cta" onClick={() => navigate("/app/numbers")}
            className="flex items-center gap-2 rounded-full bg-[#059669] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-105">
            <Plus size={16} weight="bold" /> Get a number
          </button>
        </div>
      )}

      {/* flagship services */}
      <div data-testid="home-flagship">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Featured services</h2>
          <button data-testid="home-explore-all" onClick={() => navigate("/app/more")}
            className="hidden items-center gap-1.5 text-sm font-semibold text-primary sm:inline-flex">
            Explore All Services <ArrowRight size={15} weight="bold" />
          </button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {flagship.map((a, i) => (
            <button key={a.label} data-testid={`flagship-${a.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`} onClick={() => navigate(a.to)}
              className="animate-fade-up group relative flex items-start gap-4 rounded-2xl border border-border bg-card p-5 text-left transition-all hover:-translate-y-1 hover:shadow-lg"
              style={{ animationDelay: `${i * 50}ms` }}>
              {a.badge && <span className="absolute right-3 top-3 rounded-full px-2 py-0.5 text-[9px] font-bold text-white" style={{ background: "#FF7A00" }}>{a.badge}</span>}
              <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${a.tint}`}>
                <a.icon size={24} weight="duotone" />
              </div>
              <div className="min-w-0">
                <span className="font-display text-base font-semibold">{a.label}</span>
                <p className="mt-0.5 text-xs text-muted-foreground">{a.sub}</p>
              </div>
            </button>
          ))}
        </div>
        <button data-testid="home-explore-all-mobile" onClick={() => navigate("/app/more")}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border border-border py-3 text-sm font-semibold sm:hidden">
          Explore All Services <ArrowRight size={15} weight="bold" />
        </button>
      </div>

      {/* audio & video conference */}
      <button data-testid="home-conference" onClick={() => navigate("/app/meet")}
        className="group relative w-full overflow-hidden rounded-3xl border border-white/10 bg-[#0b1020] p-6 text-left text-white transition-transform hover:-translate-y-0.5">
        <div className="grain absolute inset-0 opacity-60" />
        <div className="relative flex flex-wrap items-center justify-between gap-6">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/20 px-3 py-1 text-xs font-semibold text-primary"><VideoCamera size={14} weight="fill" /> Audio &amp; Video Conference</div>
            <h2 className="mt-3 font-display text-2xl font-semibold">Host HD meetings — right in your browser</h2>
            <p className="mt-2 text-sm text-white/60">Group video &amp; audio with screen share, live whiteboard, recording, raise-hand, a waiting-room lobby, host mute and one-tap guest links.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {["Raise hand", "Waiting-room lobby", "Mute participants", "Screen share", "Recording", "Reactions"].map((t) => (
                <span key={t} className="rounded-full bg-white/5 px-3 py-1 text-xs text-white/70">{t}</span>
              ))}
            </div>
          </div>
          <span className="flex items-center gap-2 rounded-full bg-primary px-5 py-3 font-semibold text-primary-foreground transition-transform group-hover:scale-105">
            <VideoCamera size={18} weight="fill" /> Start a meeting
          </span>
        </div>
      </button>

      {!isStaff && <ReferEarn compact />}
      {!isStaff && <AutoTopUpNudge />}

      {/* secondary + activity */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Services</h2>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-6 lg:grid-cols-3 xl:grid-cols-6">
            {secondary.map((s) => (
              <button key={s.label} data-testid={`service-${s.label.toLowerCase().replace(/ /g, "-")}`} onClick={() => navigate(s.to)}
                className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-card p-4 transition-colors hover:border-primary">
                <s.icon size={22} weight="duotone" className="text-primary" />
                <span className="text-center text-xs font-medium">{s.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Recent activity</h2>
            <button data-testid="see-billing" onClick={() => navigate("/app/billing")} className="text-xs font-semibold text-primary">View all</button>
          </div>
          <div className="space-y-2 rounded-2xl border border-border bg-card p-2">
            {entries.length === 0 && <p className="px-3 py-6 text-center text-sm text-muted-foreground">No activity yet</p>}
            {entries.map((e, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl px-3 py-2.5">
                <div className={`flex h-9 w-9 items-center justify-center rounded-full ${e.direction === "credit" ? "bg-emerald-100 text-emerald-600" : "bg-accent text-primary"}`}>
                  {e.direction === "credit" ? <ArrowDownLeft size={16} weight="bold" /> : <ArrowUpRight size={16} weight="bold" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium capitalize">{e.product?.replace("_", " ")}</p>
                  <p className="truncate text-xs text-muted-foreground">{shortDate(e.created_at)}</p>
                </div>
                <span className={`font-mono-metric text-sm font-semibold ${e.direction === "credit" ? "text-emerald-600" : ""}`}>
                  {e.direction === "credit" ? "+" : "−"}{money(e.amount_cents, e.currency)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
